import { useEffect, useState } from "react";
import { ArrowUpRight, Check, Minus } from "lucide-react";
import { fetchPackages, FALLBACK_TICKET_URL } from "../../lib/api";
import { FadeUp } from "./RevealText";
import { useLang, loc, locList, T } from "../../lib/i18n";

const Card = ({ p, i, lang, t, url }) => (
  <FadeUp
    delay={i * 0.1}
    className={`relative flex flex-col rounded-2xl p-8 md:p-10 border ${
      p.featured
        ? "bg-[#0B2530] text-white border-[#0B2530] shadow-[0_16px_48px_rgba(11,37,48,0.35)]"
        : "bg-white text-slate-900 border-slate-900/10 shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
    }`}
  >
    {p.featured && (
      <span className="absolute -top-3.5 left-8 rounded-full bg-[#FF7F50] text-white text-[10px] uppercase tracking-[0.25em] px-4 py-1.5">
        {t.popular}
      </span>
    )}
    <p className={`text-xs uppercase tracking-[0.25em] ${p.featured ? "text-[#7BDFF2]" : "text-[#00B4D8]"}`}>{p.name}</p>
    <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-2">{loc(p, "subtitle", lang)}</h3>
    <div className="mt-6 flex items-baseline gap-2">
      <span className="font-serif text-4xl md:text-5xl tracking-tight">${p.price.toLocaleString("en-US")}</span>
      <span className={`text-sm ${p.featured ? "text-white/50" : "text-slate-400"}`}>{t.perGuest}</span>
    </div>
    <p className={`text-xs uppercase tracking-[0.2em] mt-2 ${p.featured ? "text-white/40" : "text-slate-400"}`}>
      {p.quantity} {t.qty}
    </p>
    <ul className="mt-7 flex flex-col gap-3 flex-1">
      {locList(p, "includes", lang).map((inc, j) => (
        <li key={j} className="flex items-start gap-3 text-sm leading-snug">
          <Check size={16} className={`shrink-0 mt-0.5 ${p.featured ? "text-[#7BDFF2]" : "text-[#00B4D8]"}`} />
          <span className={p.featured ? "text-white/80" : "text-slate-600"}>{inc}</span>
        </li>
      ))}
    </ul>
    <a
      href={url}
      target="_blank"
      rel="noopener"
      data-testid={`package-buy-${p.slug}`}
      className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full text-xs uppercase tracking-[0.2em] px-6 py-4 transition-colors duration-300 ${
        p.featured ? "bg-[#00B4D8] hover:bg-[#33c5e2] text-white" : "bg-[#0B2530] hover:bg-[#00B4D8] text-white"
      }`}
    >
      {t.buy}
      <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
  </FadeUp>
);

const MATRIX = {
  "gala-vip": [true, false, false, false],
  "gold-vip": [true, true, false, false],
  "platinum-vip": [true, false, true, false],
  "diamond-vip": [true, true, true, true],
};

const CompareTable = ({ packages, t }) => (
  <FadeUp className="mt-16 md:mt-24">
    <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-slate-900 mb-8">{t.compareTitle}</h3>
    <div className="overflow-x-auto rounded-2xl border border-slate-900/10 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
      <table data-testid="packages-compare-table" className="w-full min-w-[720px] text-left">
        <thead>
          <tr className="border-b border-slate-900/10">
            <th className="px-6 py-5 text-[11px] uppercase tracking-[0.25em] text-slate-400 font-normal">{t.compareEvent}</th>
            {packages.map((p) => (
              <th key={p.slug} className={`px-4 py-5 text-center ${p.featured ? "bg-[#0B2530]" : ""}`}>
                <span className={`block text-[11px] uppercase tracking-[0.2em] ${p.featured ? "text-[#7BDFF2]" : "text-[#00B4D8]"}`}>{p.name}</span>
                <span className={`block font-serif text-lg mt-1 ${p.featured ? "text-white" : "text-slate-900"}`}>${p.price.toLocaleString("en-US")}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.compareRows.map((row, ri) => (
            <tr key={ri} className={ri < t.compareRows.length - 1 ? "border-b border-slate-900/5" : ""}>
              <td className="px-6 py-4 text-sm text-slate-600">{row}</td>
              {packages.map((p) => (
                <td key={p.slug} className={`px-4 py-4 text-center ${p.featured ? "bg-[#0B2530]" : ""}`}>
                  {MATRIX[p.slug]?.[ri] ? (
                    <Check size={18} className={`inline ${p.featured ? "text-[#7BDFF2]" : "text-[#00B4D8]"}`} />
                  ) : (
                    <Minus size={16} className={`inline ${p.featured ? "text-white/20" : "text-slate-300"}`} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </FadeUp>
);

export const Packages = ({ ticketUrl }) => {
  const [packages, setPackages] = useState([]);
  const { lang } = useLang();
  const t = T[lang].packages;
  const url = ticketUrl || FALLBACK_TICKET_URL;

  useEffect(() => {
    fetchPackages().then(setPackages).catch(() => {});
  }, []);

  if (!packages.length) return null;

  return (
    <section id="packages" data-testid="packages-section" className="bg-[#F4F0E8] py-24 md:py-36">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <FadeUp className="mb-14 md:mb-20">
          <p className="text-xs uppercase tracking-[0.3em] text-[#00B4D8] mb-5">{t.kicker}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-slate-900 leading-[1.05]">
            {t.title1} <span className="italic text-[#FF7F50]">{t.title2}</span>
          </h2>
          <p className="text-slate-500 text-base md:text-lg mt-6 max-w-xl">{t.sub}</p>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-7 items-stretch">
          {packages.map((p, i) => (
            <Card key={p.slug} p={p} i={i} lang={lang} t={t} url={url} />
          ))}
        </div>
        <CompareTable packages={packages} t={t} />
      </div>
    </section>
  );
};
